/**
 * component-wishlist.js
 * Global wishlist API on window.theme.wishlist.
 * Storage: localStorage array of product handles.
 * Dispatches wishlist:changed on document after every mutation.
 * Sets up event delegation for [data-wishlist-btn] clicks.
 */

const STORAGE_KEY = 'wishlist-items';

function _load() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch {
    return [];
  }
}

function _save(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* localStorage unavailable */
  }
}

function _dispatch(items) {
  document.dispatchEvent(new CustomEvent('wishlist:changed', {
    detail: { count: items.length }
  }));
}

const wishlist = {
  add(handle) {
    const items = _load();
    if (items.includes(handle)) return;
    items.push(handle);
    _save(items);
    _dispatch(items);
    /* Distinct from wishlist:product-added (a cart-add). This fires only when a
       product is added to the WISHLIST — consumed by the drawer auto-open gate
       (component-loader.js) + the <wishlist-drawer> element. Only add() fires it
       (toggle routes through add on the add branch); remove() does not. */
    document.dispatchEvent(new CustomEvent('wishlist:added', { detail: { handle: handle } }));
  },

  remove(handle) {
    let items = _load();
    items = items.filter(h => h !== handle);
    _save(items);
    _dispatch(items);
  },

  toggle(handle) {
    if (this.contains(handle)) {
      this.remove(handle);
      return false;
    }
    this.add(handle);
    return true;
  },

  contains(handle) {
    return _load().includes(handle);
  },

  getAll() {
    return _load();
  },

  getCount() {
    return _load().length;
  }
};

window.theme.wishlist = wishlist;

/* Event delegation for wishlist buttons */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-wishlist-btn]');
  if (!btn) return;

  const handle = btn.dataset.productHandle;
  if (!handle) return;

  const added = wishlist.toggle(handle);
  const label = added
    ? (btn.dataset.labelRemove || 'Remove from wishlist')
    : (btn.dataset.labelAdd || 'Add to wishlist');
  btn.classList.toggle('is-active', added);
  btn.setAttribute('aria-label', label);

  /* Announce action to screen readers */
  if (window.theme?.utilities?.announce) {
    window.theme.utilities.announce(added
      ? (btn.dataset.labelRemove || 'Remove from wishlist')
      : (btn.dataset.labelAdd || 'Add to wishlist'));
  }
});

/* Sync button states on page load and after changes */
function _syncButtons() {
  document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    const active = wishlist.contains(handle);
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-label', active
      ? (btn.dataset.labelRemove || 'Remove from wishlist')
      : (btn.dataset.labelAdd || 'Add to wishlist'));
  });
}

document.addEventListener('wishlist:changed', _syncButtons);

/* Initial dispatch so header badge can read count */
_dispatch(_load());

/* Sync buttons once DOM is ready */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _syncButtons);
} else {
  _syncButtons();
}
